// Form delivery helper for Zylus Homes
//
// HOW THIS WORKS:
// A static React frontend cannot send email directly. We support two delivery paths:
//
// 1. FORMSPREE / WEB3FORMS (recommended) — paste your endpoint URL into FORM_ENDPOINT
//    below. Both services are free and require no backend.
//    - Formspree: sign up at https://formspree.io, create a form, paste its endpoint
//      (looks like https://formspree.io/f/xxxxxxxx) and set the form's destination
//      to info@zylushomes.com. Done.
//    - Web3Forms: sign up at https://web3forms.com (no account needed for first key),
//      paste their access key URL (https://api.web3forms.com/submit) and include the
//      `access_key` in your form data.
//
// 2. MAILTO FALLBACK — if FORM_ENDPOINT is empty OR the network call fails, we open
//    the user's email client pre-filled with the form data, addressed to
//    info@zylushomes.com. Works everywhere, but depends on user having an email client.
//
// To go live: just paste your Formspree URL into FORM_ENDPOINT and ship. No other code
// changes needed.

export const ZYLUS_EMAIL = 'info@zylushomes.com'
export const FORM_ENDPOINT = '' // <-- paste your Formspree URL here (e.g. https://formspree.io/f/xxxxxxxx)

export type FormPayload = Record<string, string | number | boolean | undefined>

export type SendFormOptions = {
  /** Used for the email subject line, e.g. "Inspection Request" */
  formName: string
  /** All the field values from the form */
  data: FormPayload
}

/**
 * Submit a form to info@zylushomes.com.
 * - If FORM_ENDPOINT is configured, POSTs JSON there.
 * - Otherwise (or on failure), falls back to opening the user's mail client.
 */
export async function sendForm({
  formName,
  data,
}: SendFormOptions): Promise<{ ok: boolean; method: 'api' | 'mailto' }> {
  const cleanData: FormPayload = {
    _subject: `[Zylus Web] ${formName}`,
    _to: ZYLUS_EMAIL,
    ...data,
  }

  // 1. Try the API endpoint if configured
  if (FORM_ENDPOINT) {
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(cleanData),
      })
      if (res.ok) {
        return { ok: true, method: 'api' }
      }
    } catch {
      // fall through to mailto
    }
  }

  // 2. Mailto fallback — open the user's mail client pre-filled
  try {
    const subject = `[Zylus Web] ${formName}`
    const body = Object.entries(cleanData)
      .filter(([k]) => !k.startsWith('_'))
      .map(([k, v]) => `${prettyLabel(k)}: ${v ?? ''}`)
      .join('\n')
    const mailto = `mailto:${ZYLUS_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body + '\n\n—\nSent from zylushomes.com')}`
    window.location.href = mailto
    return { ok: true, method: 'mailto' }
  } catch {
    return { ok: false, method: 'mailto' }
  }
}

function prettyLabel(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}
